import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw, Home } from 'lucide-react'
import * as Sentry from '@sentry/react'
import { Card, CardTitle, CardDescription } from './Card'
import { buttonStyles } from './Button'

interface Props {
  children: ReactNode
  /** Render-prop fallback. Receives the error and a reset() helper. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

/**
 * Catches errors thrown by descendants in the render phase.
 * Doesn't catch async errors — for those, Sentry's global handlers will
 * pick them up (added separately).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack)
    // Explicit Sentry capture (no-op if Sentry wasn't initialized).
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
    })
  }

  reset = () => this.setState({ error: null })

  render() {
    if (!this.state.error) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback(this.state.error, this.reset)
    }

    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-2xl bg-error/10 text-error flex items-center justify-center">
            <AlertTriangle size={22} />
          </div>
          <CardTitle className="text-2xl">Something went wrong</CardTitle>
          <CardDescription className="mt-2">
            An unexpected error broke this part of the app. The team has been notified —
            try again, or head back to the dashboard.
          </CardDescription>
          {import.meta.env.DEV && (
            <pre className="mt-4 text-left text-xs text-ink-muted bg-surface-elevated rounded-xl p-3 overflow-auto max-h-40">
              {this.state.error.message}
            </pre>
          )}
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className={buttonStyles({ variant: 'secondary', size: 'md' })}
            >
              <RotateCcw size={16} className="mr-2 -ml-0.5" />
              Try again
            </button>
            <a href="/" className={buttonStyles({ variant: 'primary', size: 'md' })}>
              <Home size={16} className="mr-2 -ml-0.5" />
              Go to dashboard
            </a>
          </div>
        </Card>
      </div>
    )
  }
}
